import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import { FiFileText, FiCheckCircle, FiClock, FiArrowRight } from 'react-icons/fi';

const ContractorDashboard = () => {
  const { user, onboardingComplete, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const data = await api.getMyProfile();
      setProfile(data.contractor);
      if (data.onboarding_complete !== onboardingComplete) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Navigation />
        <div className="container mx-auto px-4 py-12">
          <div className="text-white text-center">Loading...</div>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const badges = {
      draft: (
        <span className="px-3 py-1 bg-slate-500/20 text-slate-300 rounded-full text-sm font-medium">
          Draft
        </span>
      ),
      pending: (
        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm font-medium flex items-center gap-1">
          <FiClock className="w-4 h-4" />
          Pending Review
        </span>
      ),
      approved: (
        <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm font-medium flex items-center gap-1">
          <FiCheckCircle className="w-4 h-4" />
          Approved
        </span>
      ),
      rejected: (
        <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm font-medium">
          Rejected
        </span>
      ),
    };
    return badges[status] || badges.draft;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navigation />
      <div className="container mx-auto px-4 py-12 pb-24">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-white mb-2">Welcome back, {user?.username}!</h1>
          <p className="text-slate-300 mb-8">Manage your contractor profile and onboarding</p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">Onboarding Status</h2>
                <FiFileText className="w-6 h-6 text-blue-400" />
              </div>
              {onboardingComplete ? (
                <div>
                  <div className="mb-4">{getStatusBadge(profile?.status || 'draft')}</div>
                  {profile?.application_id && (
                    <p className="text-slate-300 text-sm">
                      Application ID: <span className="font-mono">{profile.application_id}</span>
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-slate-300 mb-4">
                    Complete your onboarding to start working with us.
                  </p>
                  <button
                    onClick={() => navigate('/onboarding')}
                    className="w-full py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    Start Onboarding
                    <FiArrowRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">Quick Actions</h2>
              </div>
              <div className="space-y-3">
                {!onboardingComplete ? (
                  <button
                    onClick={() => navigate('/onboarding')}
                    className="w-full py-2 glass-button text-white text-left px-4 hover:bg-white/30"
                  >
                    Complete Onboarding
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/onboarding')}
                    className="w-full py-2 glass-button text-white text-left px-4 hover:bg-white/30"
                    disabled
                  >
                    Onboarding Complete
                  </button>
                )}
              </div>
            </div>
          </div>

          {profile && (
            <div className="glass-card p-6">
              <h2 className="text-xl font-semibold text-white mb-4">Profile Summary</h2>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Full Name:</span>
                  <span className="text-white ml-2">{profile.full_name || 'Not set'}</span>
                </div>
                <div>
                  <span className="text-slate-400">Email:</span>
                  <span className="text-white ml-2">{profile.email || 'Not set'}</span>
                </div>
                <div>
                  <span className="text-slate-400">Telephone:</span>
                  <span className="text-white ml-2">{profile.telephone || 'Not set'}</span>
                </div>
                <div>
                  <span className="text-slate-400">Business Name:</span>
                  <span className="text-white ml-2">{profile.business_name || 'Not set'}</span>
                </div>
                {(profile.preferred_drink || profile.drink_notes) && (
                  <div className="md:col-span-2 space-y-1">
                    <span className="text-slate-400 block">Drink:</span>
                    <span className="text-white">
                      {profile.preferred_drink || ''}
                      {profile.preferred_drink && profile.drink_notes ? ' – ' : ''}
                      {profile.drink_notes || ''}
                    </span>
                  </div>
                )}
                {((Array.isArray(profile.insurances_file_path) && profile.insurances_file_path.length > 0) ||
                  (Array.isArray(profile.qualifying_bodies_file_path) && profile.qualifying_bodies_file_path.length > 0)) && (
                  <div className="md:col-span-2 space-y-2">
                    <span className="text-slate-400 block">Documents:</span>
                    <div className="flex flex-wrap gap-2">
                      {Array.isArray(profile.insurances_file_path) &&
                        profile.insurances_file_path.map((filename) => (
                          <a key={filename} href={api.getUploadUrl(filename)} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline text-sm">
                            {filename}
                          </a>
                        ))}
                      {Array.isArray(profile.qualifying_bodies_file_path) &&
                        profile.qualifying_bodies_file_path.map((filename) => (
                          <a key={filename} href={api.getUploadUrl(filename)} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline text-sm">
                            {filename}
                          </a>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractorDashboard;
